import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { admin } from '../services/api';
import { PRIORITES, STATUTS_PROJET, STATUTS_TACHE } from '../constants';
import { BadgePriorite } from '../components/communs';
import { horodatage } from '../utils/format';

const SECTIONS = [
  ['principe', 'Le principe'],
  ['roles', 'Qui voit quoi'],
  ['kanban', 'Kanbans et fresque'],
  ['taches', 'Tâches et priorités'],
  ['pieces', 'Pièces jointes'],
  ['mur', 'Mur d’activité et mentions'],
  ['notifications', 'Notifications'],
  ['parametres', 'Paramètres'],
  ['crm', 'Lien avec le CRM'],
  ['rgpd', 'Données personnelles'],
];

const TUNNEL = [
  ['Concession ou client', 'Repris du CRM ou créé dans les Paramètres. Un projet s’y rattache.'],
  ['Projet', 'Un responsable, des membres, une échéance. Il avance de colonne en colonne.'],
  ['Tâches', 'Assignées à une personne, avec une priorité et une échéance.'],
  ['Mur d’activité', 'On échange sur la tâche, on mentionne avec @ : la personne est prévenue.'],
];

function Etape({ titre, detail, derniere }) {
  return (
    <div className="doc-tunnel-etape">
      <div className="doc-tunnel-bloc">
        <div className="doc-tunnel-titre">{titre}</div>
        <div className="doc-tunnel-detail">{detail}</div>
      </div>
      {!derniere && <span className="doc-tunnel-fleche" aria-hidden="true">→</span>}
    </div>
  );
}

// Documentation — même gabarit que celle d'A97 Hub : un titre, le « tunnel »
// en tête, puis une section par sujet. L'état des liaisons est lu en direct
// pour les administrateurs.
export default function Documentation({ moi }) {
  const [etat, setEtat] = useState(null);
  useEffect(() => { if (moi.role === 'admin') admin.etat().then(setEtat).catch(() => setEtat(null)); }, [moi.role]);

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Documentation — A97 Projets</h1>
          <p>Comment l’application est organisée, qui voit quoi, et comment on s’y prévient les uns les autres.</p>
        </div>
      </div>
      <nav className="doc-sommaire" aria-label="Sommaire">
        {SECTIONS.map(([id, t]) => <a key={id} href={`#${id}`}>{t}</a>)}
      </nav>

      <h2 className="doc-h2" id="principe">Le principe</h2>
      <div className="doc-tunnel">
        {TUNNEL.map(([t, d], i) => <Etape key={t} titre={t} detail={d} derniere={i === TUNNEL.length - 1} />)}
      </div>

      <h2 className="doc-h2" id="roles">Qui voit quoi</h2>
      <div className="table-container">
        <table className="table-cartes">
          <thead><tr><th>Rôle</th><th>Voit</th><th>Peut</th></tr></thead>
          <tbody>
            <tr>
              <td style={{ fontWeight: 600 }}>Administrateur</td>
              <td>Tous les projets et toutes les tâches du groupe. En arrivant, le tableau de bord et la page Projets montrent ses propres projets ; « Tout le groupe » et « Tout le monde » élargissent.</td>
              <td>Tout modifier, gérer les Paramètres (concessions, clients, profils, synchronisation CRM).</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 600 }}>Membre</td>
              <td>Les projets dont il est responsable ou membre, ou dans lesquels une tâche lui est assignée — et toutes les tâches de ces projets. Son tableau de bord ne porte que sur ce périmètre.</td>
              <td>Créer des projets (il en devient responsable), créer et modifier les tâches des projets qu’il voit, écrire sur leurs murs. Modifier, déplacer ou supprimer un projet : responsable du projet seulement.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="doc-texte faible" style={{ marginTop: 8 }}>
        Le contrôle est fait par le serveur à chaque action, avec le rôle relu en base : retirer un droit ou désactiver un profil prend effet immédiatement.
        Assigner une tâche à quelqu’un ou le mentionner l’ajoute aux membres du projet, pour qu’il puisse ouvrir ce qu’on lui signale.
      </p>

      <h2 className="doc-h2" id="kanban">Kanbans et fresque</h2>
      <div className="doc-texte">
        <p>Deux tableaux, même geste : <b>glisser une carte</b> d’une colonne à l’autre change son statut, la lâcher entre deux cartes fixe son rang.
          Sur téléphone, ouvrez la carte et changez le statut dans la fiche.</p>
        <ul className="doc-etapes">
          <li><Link to="/projets">Projets</Link> : {STATUTS_PROJET.map((s) => s.libelle).join(' → ')}. Filtres par concession / client, par responsable, archivés ; vue liste disponible.</li>
          <li>Dans un projet, les tâches : {STATUTS_TACHE.map((s) => s.libelle).join(' → ')}. Filtres par personne et par priorité.</li>
        </ul>
        <p>Un projet terminé reste dans sa colonne ; pour le sortir du tableau sans le perdre, <b>archivez-le</b> (bouton Modifier du projet).</p>
        <p>La vue <b>Fresque</b> (bouton à droite de Kanban et Liste) montre les projets dans le temps sur une frise de mois, avec la ligne rouge
          d’aujourd’hui : une ligne par projet, puis <b>une ligne par tâche datée</b> juste en dessous. Une tâche qui a un <b>début et une échéance</b>
          devient une barre ; une tâche avec <b>une seule date</b> devient un jalon (épingle) ; une tâche sans date est comptée sous le nom du projet.
          Couleurs : bleu à faire ou en cours, orange priorité haute, rouge en retard (« ! »), vert terminée. La bande grise de la ligne du projet
          marque sa période (début → échéance).</p>
        <p><b>Replanifier à la souris</b> : glissez une barre pour la déplacer (la durée est conservée), tirez son <b>bord gauche</b> pour changer le
          début ou son <b>bord droit</b> pour changer l’échéance ; un jalon se glisse de la même façon. Pendant le geste, une bulle affiche la date
          visée et la durée ; la frise défile toute seule au bord. Échap annule. Le changement est enregistré au lâcher et tracé dans le mur de la tâche.</p>
        <p>Le curseur Zoom resserre ou étire la frise, « Aller à aujourd’hui » recentre ; un clic sur une barre ou un nom de tâche ouvre la tâche,
          un clic sur le nom du projet ouvre le projet. Les filtres de la page s’appliquent aussi à la fresque.</p>
      </div>

      <h2 className="doc-h2" id="taches">Tâches et priorités</h2>
      <div className="doc-texte">
        <p>Une tâche a un titre, une description, une personne assignée, une échéance et une priorité :</p>
        <p style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{PRIORITES.map((p) => <BadgePriorite key={p.cle} priorite={p.cle} />)}</p>
        <p>La priorité est <b>Faible par défaut</b>. « Mes tâches » et le tableau de bord classent par priorité puis par échéance ; une échéance dépassée s’affiche en rouge.</p>
      </div>

      <h2 className="doc-h2" id="pieces">Pièces jointes</h2>
      <div className="doc-texte">
        <p>Dans la fiche d’une tâche, « Joindre un fichier » (ou un glisser-déposer sur le bloc) ajoute un ou plusieurs fichiers, <b>10 Mo au plus</b> chacun.
          L’ajout est enregistré tout de suite et tracé dans le mur.</p>
        <p>Un clic sur le nom ouvre l’aperçu : images, PDF, vidéos, sons et textes s’affichent ; <kbd>←</kbd> <kbd>→</kbd> passent au fichier voisin.
          Les autres formats (Word, Excel, archives…) se téléchargent avec l’icône de téléchargement.</p>
        <p>Tous ceux qui voient la tâche voient ses fichiers. Peuvent retirer un fichier : celui qui l’a joint, le responsable du projet, un administrateur.
          Supprimer la tâche supprime ses fichiers.</p>
      </div>

      <h2 className="doc-h2" id="mur">Mur d’activité et mentions</h2>
      <div className="doc-texte">
        <p>Chaque tâche a son fil de discussion, en bas de sa fiche. On y lit aussi l’historique : création, changement de statut, de priorité, d’échéance, d’assignation.</p>
        <ol className="doc-etapes">
          <li>Tapez <kbd>@</kbd> puis le début d’un prénom ou d’un nom : la liste des profils s’ouvre.</li>
          <li>Choisissez avec <kbd>↑</kbd> <kbd>↓</kbd> puis <kbd>Entrée</kbd> (ou à la souris).</li>
          <li>Envoyez avec le bouton ou <kbd>Ctrl</kbd>+<kbd>Entrée</kbd>. La personne mentionnée est prévenue dans l’application et par e-mail.</li>
        </ol>
        <p>On peut supprimer ses propres messages ; un administrateur peut supprimer n’importe quel message.</p>
      </div>

      <h2 className="doc-h2" id="notifications">Notifications</h2>
      <div className="table-container">
        <table className="table-cartes">
          <thead><tr><th>Événement</th><th>Qui est prévenu</th><th>Cloche</th><th>E-mail</th></tr></thead>
          <tbody>
            <tr><td>Une tâche vous est assignée</td><td>La personne assignée</td><td>Oui</td><td>Oui</td></tr>
            <tr><td>Vous êtes nommé responsable d’un projet</td><td>Le nouveau responsable</td><td>Oui</td><td>Oui</td></tr>
            <tr><td>On vous mentionne avec @</td><td>Les personnes mentionnées</td><td>Oui</td><td>Oui</td></tr>
            <tr><td>Nouveau message sur une tâche</td><td>La personne assignée, l’auteur de la tâche, ceux qui y ont déjà écrit</td><td>Oui</td><td>Non</td></tr>
            <tr><td>Une tâche change de statut</td><td>La personne assignée, l’auteur de la tâche</td><td>Oui</td><td>Non</td></tr>
            <tr><td>On vous ajoute à un projet</td><td>Les nouveaux membres</td><td>Oui</td><td>Non</td></tr>
          </tbody>
        </table>
      </div>
      <p className="doc-texte faible" style={{ marginTop: 8 }}>
        On n’est jamais prévenu de ses propres actions. La cloche se met à jour toutes les 30 secondes ; un clic sur une notification ouvre la tâche.
        Chacun peut couper les e-mails depuis son profil (rond en haut à droite).
      </p>

      <h2 className="doc-h2" id="parametres">Paramètres (administrateurs)</h2>
      <div className="doc-texte">
        <ul className="doc-etapes">
          <li><b>Concessions</b> et <b>Clients</b> : ajouter, renommer, désactiver. Un client peut être rattaché à une concession ; filtrer les projets sur une concession montre aussi ceux de ses clients. Une fiche à laquelle des projets sont rattachés ne se supprime pas : on la désactive.</li>
          <li><b>Profils</b> : créer un profil (prénom, nom, e-mail Microsoft, rôle), changer un rôle, désactiver un accès. Pas de mot de passe : on se connecte avec Microsoft.</li>
          <li><b>Intégrations</b> : état de la synchronisation CRM, des e-mails et de la connexion Microsoft ; synchronisation à la demande.</li>
        </ul>
      </div>

      <h2 className="doc-h2" id="crm">Lien avec le CRM autodom97</h2>
      <div className="doc-texte">
        <p>La liste des utilisateurs est celle du CRM : utilisateurs, concessions et clients sont repris par <b>l’API du CRM</b> — A97 Projets ne lit jamais la base du CRM (règle A97 : un produit, sa base).
          Synchronisation au démarrage puis toutes les 6 heures, ou à la demande depuis les Paramètres.</p>
        <ul className="doc-etapes">
          <li>Un utilisateur actif du CRM devient un profil « Membre » (le rôle se règle ensuite ici et n’est plus touché par la synchronisation).</li>
          <li>Un utilisateur désactivé ou supprimé dans le CRM est désactivé ici.</li>
          <li>Les profils créés à la main ne sont jamais modifiés par la synchronisation.</li>
          <li>Concessions et clients repris gardent le nom tenu par le CRM ; disparus du CRM, ils deviennent inactifs (jamais supprimés, des projets peuvent y être rattachés).</li>
        </ul>
        {etat && (
          <p>
            État actuel : {etat.crm.configure ? <span className="badge badge-succes">synchronisation configurée</span> : <span className="badge badge-alerte">synchronisation non configurée</span>}
            {etat.crm.derniere && <> — dernière le {horodatage(etat.crm.derniere.le)} ({etat.crm.derniere.ok ? 'réussie' : `échec : ${etat.crm.derniere.erreur}`})</>}
            {' · '}{etat.mail ? <span className="badge badge-succes">e-mails actifs</span> : <span className="badge badge-alerte">e-mails non configurés</span>}
          </p>
        )}
      </div>

      <h2 className="doc-h2" id="rgpd">Données personnelles</h2>
      <div className="doc-texte">
        <p>A97 Projets conserve, pour chaque profil : prénom, nom, adresse e-mail professionnelle, rôle, date de dernière connexion et préférence d’e-mail.
          Ce sont les données nécessaires pour se connecter, assigner, mentionner et prévenir. Le contenu des tâches, des messages et des pièces jointes est celui que chacun y dépose :
          n’y mettez pas de données personnelles de clients au-delà du nécessaire.</p>
        <p>Les notifications lues sont effacées après 90 jours. Un profil désactivé ne peut plus se connecter ; ses messages restent signés de son nom.</p>
      </div>
    </div>
  );
}
